// Analytics abstraction - Privacy-respecting conversion tracking
// NO personal health info, NO form field contents, NO sensitive data

const ANALYTICS_VERSION = '1.0.0';
const STORAGE_KEY = 'xion_analytics_events';
const DEV_MODE = process.env.NODE_ENV === 'development';

// Event types
export const EVENTS = {
  CTA_CONTACT_PH_CLICK: 'cta_contact_ph_click',
  CTA_VISIT_FMS_CLICK: 'cta_visit_fms_click',
  FORM_SUBMIT_CONTACT_PH: 'form_submit_contact_ph',
  HOTSPOT_CLICK: 'hotspot_click',
  PRODUCT_CARD_INQUIRE_CLICK: 'product_card_inquire_click',
  PAGE_VIEW: 'page_view',
  REDUCED_MOTION_TOGGLE: 'reduced_motion_toggle',
};

// Get stored event counts
const getStoredCounts = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Store event counts
const storeEventCounts = (counts) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
  } catch (e) {
    console.warn('Analytics: Could not store event counts', e);
  }
};

// Main tracking function
export const track = (eventName, props = {}) => {
  // Sanitize props - only allow safe properties
  const safeProps = {
    pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
    componentName: props.componentName || 'unknown',
    timestamp: new Date().toISOString(),
    // Only include non-sensitive metadata
    ...(props.hotspotId && { hotspotId: props.hotspotId }),
    ...(props.productCategory && { productCategory: props.productCategory }),
    ...(props.systemType && { systemType: props.systemType }),
  };

  // Log to console in dev mode
  if (DEV_MODE || window.__XION_ANALYTICS_DEBUG__) {
    console.log(`%c[Analytics] ${eventName}`, 'color: #00FFD1; font-weight: bold', safeProps);
  }

  // Update local storage counts for sanity checking
  const counts = getStoredCounts();
  counts[eventName] = (counts[eventName] || 0) + 1;
  counts._lastEvent = { name: eventName, props: safeProps, time: safeProps.timestamp };
  counts._totalEvents = (counts._totalEvents || 0) + 1;
  storeEventCounts(counts);

  // Dispatch custom event for any external listeners
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('xion_analytics', {
      detail: { eventName, props: safeProps }
    }));
  }

  return { eventName, props: safeProps };
};

// Track page view
export const trackPageView = (pageName) => {
  return track(EVENTS.PAGE_VIEW, { componentName: pageName });
};

// Track CTA clicks
export const trackCTAClick = (ctaType, componentName) => {
  const eventName = ctaType === 'fms' ? EVENTS.CTA_VISIT_FMS_CLICK : EVENTS.CTA_CONTACT_PH_CLICK;
  return track(eventName, { componentName });
};

// Track form submission (no form data!)
export const trackFormSubmit = (formName) => {
  return track(EVENTS.FORM_SUBMIT_CONTACT_PH, { componentName: formName });
};

// Track hotspot interaction
export const trackHotspotClick = (hotspotId, hotspotName) => {
  return track(EVENTS.HOTSPOT_CLICK, { hotspotId, componentName: hotspotName });
};

// Track product card inquire click
export const trackProductInquire = (productCategory, productName) => {
  return track(EVENTS.PRODUCT_CARD_INQUIRE_CLICK, { productCategory, componentName: productName });
};

// Get analytics summary for dev dashboard
export const getAnalyticsSummary = () => {
  const counts = getStoredCounts();
  return {
    version: ANALYTICS_VERSION,
    totalEvents: counts._totalEvents || 0,
    lastEvent: counts._lastEvent || null,
    eventCounts: Object.entries(counts)
      .filter(([key]) => !key.startsWith('_'))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
  };
};

// Clear analytics data
export const clearAnalytics = () => {
  localStorage.removeItem(STORAGE_KEY);
  console.log('%c[Analytics] Cleared all stored data', 'color: #00FFD1');
};

// Enable debug mode
export const enableDebug = () => {
  window.__XION_ANALYTICS_DEBUG__ = true;
  console.log('%c[Analytics] Debug mode enabled', 'color: #00FFD1; font-weight: bold');
};

export default {
  track,
  trackPageView,
  trackCTAClick,
  trackFormSubmit,
  trackHotspotClick,
  trackProductInquire,
  getAnalyticsSummary,
  clearAnalytics,
  enableDebug,
  EVENTS
};
